import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../lib/format';
import {
  DollarSign,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Package,
  User,
  Building2,
  ChevronLeft,
  ChevronRight,
  Printer,
  ShoppingCart,
  CreditCard
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { InvoicePaymentModal } from '../components/payments/InvoicePaymentModal';
import { toast } from '../lib/toast';
import { Invoice, User as IUser } from '@bloom/shared';
import { formatDate as formatDateUtil } from '../utils/dateUtils';

interface PhysicianWithAM extends Omit<IUser, 'physicianData'> {
  physicianData?: {
    npiNumber?: string;
    distributorId?: {
      _id: string;
      profile?: { firstName?: string; lastName?: string };
      email?: string;
      distributorData?: { companyName?: string };
    };
    clinicAddress?: {
      street?: string;
      street2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
  };
}

interface PopulatedInvoice extends Omit<Invoice, 'physicianId' | 'createdById'> {
  physicianId?: PhysicianWithAM;
  createdById?: IUser;
}

// Invoice tracking item interface - all items are invoices
interface PaymentTrackingItem {
  _id: string;
  type: 'bulk' | 'patient';  // Invoice type based on linked order
  displayNumber: string;
  createdAt: string;
  totalAmount: number;
  status: string;
  paymentStatus: string;
  amountPaid: number;
  amountDue: number;
  paidAt?: string;
  dueDate?: string;
  creditApplied?: number;
  fulfillmentStatus?: string;
  physicianId?: {
    _id: string;
    email: string;
    profile?: { firstName?: string; lastName?: string; clinicName?: string };
  };
}

interface PaymentTrackingStats {
  totalInvoices: number;
  totalOrders: number;  // Deprecated - kept for backwards compatibility
  paidCount: number;
  pendingCount: number;
  outstandingAmount: number;
  creditBalance?: number;
}

// Helper to get Account Manager name from physician's distributorId
const getAccountManagerName = (invoice: PopulatedInvoice): string => {
  const am = invoice.physicianId?.physicianData?.distributorId;
  if (!am) return '-';
  
  // Try company name first, then full name
  if (am.distributorData?.companyName) {
    return am.distributorData.companyName;
  }
  if (am.profile?.firstName || am.profile?.lastName) {
    return `${am.profile?.firstName || ''} ${am.profile?.lastName || ''}`.trim();
  }
  return '-';
};

const getAccountManagerEmail = (invoice: PopulatedInvoice): string => {
  const am = invoice.physicianId?.physicianData?.distributorId;
  return am?.email || '';
};

// Print function for invoices
const printInvoice = (invoice: PopulatedInvoice) => {

  const formatDate = (date: string | Date) => formatDateUtil(date, { month: 'long' });

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice ${invoice.invoiceNumber}</title>
      <style>
        @page {
          size: 8.5in 11in;
          margin: 1in;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
        body {
          font-family: Arial, sans-serif;
          line-height: 1.4;
          color: #000;
          padding: 20px;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
        }
        .invoice-title {
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .invoice-number {
          font-size: 18px;
          color: #666;
        }
        .invoice-details {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
        }
        .billing-info, .invoice-info {
          width: 48%;
        }
        .section-title {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #333;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
        }
        .items-table th,
        .items-table td {
          border: 1px solid #ddd;
          padding: 8px 12px;
          text-align: left;
        }
        .items-table th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        .items-table .text-right {
          text-align: right;
        }
        .totals {
          width: 300px;
          margin-left: auto;
          margin-bottom: 30px;
        }
        .totals table {
          width: 100%;
          border-collapse: collapse;
        }
        .totals td {
          padding: 5px 10px;
          border-bottom: 1px solid #eee;
        }
        .totals .total-row {
          font-weight: bold;
          border-top: 2px solid #333;
          border-bottom: 2px solid #333;
        }
        .payment-status {
          background-color: #f0f9ff;
          border: 1px solid #0ea5e9;
          border-radius: 4px;
          padding: 15px;
          margin-top: 20px;
        }
        .paid-status {
          background-color: #f0fdf4;
          border-color: #22c55e;
        }
        .overdue-status {
          background-color: #fef2f2;
          border-color: #ef4444;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="invoice-title">INVOICE</div>
        <div class="invoice-number">${invoice.invoiceNumber}</div>
      </div>

      <div class="invoice-details">
        <div class="billing-info">
          <div class="section-title">Bill To:</div>
          <div>Dr. ${invoice.physicianId?.profile?.firstName} ${invoice.physicianId?.profile?.lastName}</div>
          ${invoice.physicianId?.profile?.clinicName ? `<div>${invoice.physicianId.profile.clinicName}</div>` : ''}
          ${invoice.physicianId?.physicianData?.clinicAddress ? `
            <div>${invoice.physicianId.physicianData.clinicAddress.street || ''}</div>
            ${invoice.physicianId.physicianData.clinicAddress.street2 ? `<div>${invoice.physicianId.physicianData.clinicAddress.street2}</div>` : ''}
            <div>${invoice.physicianId.physicianData.clinicAddress.city || ''}, ${invoice.physicianId.physicianData.clinicAddress.state || ''} ${invoice.physicianId.physicianData.clinicAddress.zipCode || ''}</div>
          ` : ''}
          <div>${invoice.physicianId?.email}</div>
        </div>
        <div class="invoice-info">
          <div class="section-title">Invoice Details:</div>
          <div><strong>Date:</strong> ${formatDate(invoice.createdAt)}</div>
          <div><strong>Due Date:</strong> ${formatDate(invoice.dueDate)}</div>
          <div><strong>Payment Terms:</strong> ${invoice.paymentTerms}</div>
          <div><strong>Status:</strong> ${invoice.status.toUpperCase()}</div>
        </div>
      </div>

      <table class="items-table">
        <thead>
          <tr>
            <th>Description</th>
            <th class="text-right">Quantity</th>
            <th class="text-right">Unit Price</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td class="text-right">${item.quantity}</td>
              <td class="text-right">${formatCurrency(item.unitPrice)}</td>
              <td class="text-right">${formatCurrency(item.totalPrice)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="totals">
        <table>
          <tr>
            <td>Subtotal:</td>
            <td class="text-right">${formatCurrency(invoice.subtotal)}</td>
          </tr>
          ${invoice.shippingAmount && invoice.shippingAmount > 0 ? `
          <tr>
            <td>Shipping:</td>
            <td class="text-right">${formatCurrency(invoice.shippingAmount)}</td>
          </tr>
          ` : ''}
          ${(invoice.telemedNetworkFee || 0) > 0 ? `
          <tr>
            <td>Telemed network fee:</td>
            <td class="text-right">${formatCurrency(invoice.telemedNetworkFee || 0)}</td>
          </tr>
          ` : ''}
          <tr>
            <td>Tax:</td>
            <td class="text-right">${formatCurrency(invoice.taxAmount)}</td>
          </tr>
          ${(invoice as any).volumeReward?.discountAmount ? `
          <tr>
            <td>Volume Reward (${(invoice as any).volumeReward.tierLabel} ${(invoice as any).volumeReward.discountPercent}%)${(invoice as any).volumeReward.appliedAs === 'physician_markup' ? ' — physician markup credit' : ''}:</td>
            <td class="text-right">${(invoice as any).volumeReward.appliedAs === 'physician_markup' ? '+' : '−'}${formatCurrency((invoice as any).volumeReward.discountAmount)}</td>
          </tr>
          ` : ''}
          ${(invoice as any).volumeReward?.excludedDealSubtotal ? `
          <tr>
            <td colspan="2" style="font-size: 11px; color: #666; font-style: italic; padding-top: 0;">
              ${formatCurrency((invoice as any).volumeReward.excludedDealSubtotal)} of negotiated-deal pricing is excluded from the volume reward.
            </td>
          </tr>
          ` : ''}
          <tr class="total-row">
            <td>Total:</td>
            <td class="text-right">${formatCurrency(invoice.totalAmount)}</td>
          </tr>
          <tr>
            <td>Amount Paid:</td>
            <td class="text-right">${formatCurrency(invoice.amountPaid)}</td>
          </tr>
          <tr>
            <td><strong>Amount Due:</strong></td>
            <td class="text-right"><strong>${formatCurrency(invoice.amountDue)}</strong></td>
          </tr>
        </table>
      </div>

      ${invoice.status === 'paid' && invoice.paidAt ? `
        <div class="payment-status paid-status">
          <strong>✓ PAID IN FULL</strong><br>
          Payment completed on ${formatDate(invoice.paidAt)}
        </div>
      ` : invoice.status === 'overdue' ? `
        <div class="payment-status overdue-status">
          <strong>⚠ OVERDUE</strong><br>
          Payment was due on ${formatDate(invoice.dueDate)}
        </div>
      ` : ''}
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
};

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const InvoiceTracking: React.FC = () => {
  const user = useUser();
  const [items, setItems] = useState<PaymentTrackingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<PopulatedInvoice | null>(null);
  // A facility_shipping invoice has NO line items — its whole value is carrier
  // cost. The parcels live on the shipment, so fetch them to render something
  // other than an empty Items table.
  const [invoiceShipment, setInvoiceShipment] = useState<any | null>(null);
  const [selectedItem, setSelectedItem] = useState<PaymentTrackingItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState<PaymentTrackingItem | null>(null);
  const [stats, setStats] = useState<PaymentTrackingStats>({
    totalInvoices: 0,
    totalOrders: 0,
    paidCount: 0,
    pendingCount: 0,
    outstandingAmount: 0
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Check for payment success/failure from Hyperswitch redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    const paymentStatus = params.get('payment');
    const invoiceNumber = params.get('invoice');

    // Clean up URL params (but not invoiceId - that's handled separately)
    if (redirectStatus || paymentStatus || invoiceNumber) {
      const url = new URL(window.location.href);
      url.searchParams.delete('redirect_status');
      url.searchParams.delete('payment');
      url.searchParams.delete('invoice');
      window.history.replaceState({}, '', url.toString());
    }

    // Show appropriate toast based on payment result
    if (redirectStatus === 'succeeded' || paymentStatus === 'success') {
      toast.success(
        invoiceNumber
          ? `Payment successful for invoice ${invoiceNumber}`
          : 'Payment processed successfully'
      );
    } else if (redirectStatus === 'failed') {
      toast.error('Payment failed. Please try again.');
    } else if (redirectStatus === 'requires_payment_method') {
      toast.error('Payment could not be processed. Please try a different payment method.');
    }
  }, []);

  // Handle invoiceId query param to auto-open invoice details
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invoiceId = params.get('invoiceId');

    if (invoiceId && !loading) {
      // Clean up URL param
      window.history.replaceState({}, '', window.location.pathname);

      // Fetch invoice details and open modal
      const fetchAndSelectInvoice = async () => {
        try {
          const response = await apiClient.get<{ success: boolean; data: PopulatedInvoice }>(`/invoices/${invoiceId}`);
          if (response.success && response.data) {
            setSelectedInvoice(response.data);
            void loadShipmentForInvoice(response.data);
            setShowDetailsModal(true);
          } else {
            toast.error('Invoice not found');
          }
        } catch (error) {
          console.error('Error fetching invoice:', error);
          toast.error('Failed to load invoice details');
        }
      };

      fetchAndSelectInvoice();
    }
  }, [loading]);

  // Fetch payment tracking data on component mount
  useEffect(() => {
    loadPaymentTracking(1);
  }, []);

  const loadPaymentTracking = async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<{
        success: boolean;
        data: PaymentTrackingItem[];
        stats: PaymentTrackingStats;
        pagination: PaginationInfo;
        error?: string;
      }>(`/invoices/payment-tracking?page=${page}&limit=20`);
      if (response.success) {
        setItems(response.data);
        if (response.stats) {
          setStats(response.stats);
        }
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError('Failed to load payment data');
      }
    } catch (err) {
      console.error('Error loading payment tracking:', err);
      setError('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    loadPaymentTracking(newPage);
  };

  const loadShipmentForInvoice = async (invoice: any) => {
    setInvoiceShipment(null);
    if (invoice?.type !== 'facility_shipping') return;
    const res = await apiClient.getShipmentForInvoice(invoice._id);
    if (res.success) setInvoiceShipment((res as any).data || null);
  };

  const openInvoiceDetail = async (invoice: PopulatedInvoice) => {
    try {
      setLoading(true);
      // Fetch full invoice details with populated fields
      const responseData = await apiClient.get(`/invoices/${invoice._id}`);
      
      if (responseData.success) {
        setSelectedInvoice(responseData.data);
        void loadShipmentForInvoice(responseData.data);
        setShowDetailsModal(true);
      } else {
        setError(`Failed to load invoice details: ${responseData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error loading invoice details:', err);
      setError(`Failed to load invoice details: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (date: string | Date) => formatDateUtil(date);

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'paid':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'sent':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'overdue':
        return `${baseClasses} bg-red-100 text-red-800`;
      case 'voided':
      case 'cancelled':
        return `${baseClasses} bg-gray-100 text-gray-600`;
      case 'refunded':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  // Get type badge for invoice
  const getTypeBadge = (type: 'bulk' | 'patient') => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    if (type === 'bulk') {
      return `${baseClasses} bg-blue-100 text-blue-800`;
    }
    return `${baseClasses} bg-purple-100 text-purple-800`;
  };

  if (!user) {
    return <div>Please log in to view invoices.</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Invoice Tracking</h1>
        <p className="text-gray-600">
          {user.role === 'physician' ?
            'Track your invoices and payment status' :
            'Monitor invoices for physicians you manage'
          }
        </p>
      </div>

      {/* Summary Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 ${stats.creditBalance && stats.creditBalance > 0 ? 'lg:grid-cols-6' : 'lg:grid-cols-5'}`}>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Bulk Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalInvoices}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Patient Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Paid</p>
              <p className="text-2xl font-bold text-gray-900">{stats.paidCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Outstanding</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.outstandingAmount)}</p>
            </div>
          </div>
        </div>

        {stats.creditBalance != null && stats.creditBalance > 0 && (
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Credit Balance</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.creditBalance)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 hover:text-red-500"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Payment Tracking Table */}
      <div className="data-table-container">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Payment Tracking ({items.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading payment data...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
            <p>You don't have any invoices yet.</p>
          </div>
        ) : (
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Fulfillment</th>
                  <th>Amount</th>
                  <th>Date</th>
                  <th>Details</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item._id}>
                    {/* Reference Number */}
                    <td className="cell-primary">
                      <div className="cell-inline">
                        {item.type === 'bulk' ? (
                          <FileText className="cell-inline-icon text-blue-500" />
                        ) : (
                          <ShoppingCart className="cell-inline-icon text-purple-500" />
                        )}
                        <div className="cell-stack">
                          <span className="cell-stack-value">{item.displayNumber}</span>
                          <span className="cell-stack-label">Created {formatDate(item.createdAt)}</span>
                        </div>
                      </div>
                    </td>
                    {/* Type Badge */}
                    <td>
                      <span className={getTypeBadge(item.type)}>
                        {item.type === 'bulk' ? 'Bulk' : 'Patient'}
                      </span>
                    </td>
                    {/* Status */}
                    <td>
                      <div className="cell-stack">
                        <span className={getStatusBadge(item.paymentStatus)}>
                          {item.paymentStatus.charAt(0).toUpperCase() + item.paymentStatus.slice(1).replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    {/* Fulfillment Status */}
                    <td>
                      {item.fulfillmentStatus ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.fulfillmentStatus === 'delivered' ? 'bg-green-100 text-green-800' :
                          item.fulfillmentStatus === 'shipped' ? 'bg-blue-100 text-blue-800' :
                          item.fulfillmentStatus === 'processing' ? 'bg-purple-100 text-purple-800' :
                          item.fulfillmentStatus === 'submitted' ? 'bg-indigo-100 text-indigo-800' :
                          item.fulfillmentStatus === 'awaiting_payment' ? 'bg-yellow-100 text-yellow-800' :
                          item.fulfillmentStatus === 'cancelled' || item.fulfillmentStatus === 'voided' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {item.fulfillmentStatus.replace(/_/g, ' ')}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    {/* Amount */}
                    <td className="cell-mono">
                      <div className="cell-stack">
                        <span className="cell-stack-value">{formatCurrency(item.totalAmount)}</span>
                        {item.creditApplied != null && item.creditApplied > 0 && (
                          <span className="cell-stack-label text-green-600">Credit: -{formatCurrency(item.creditApplied)}</span>
                        )}
                        {!['paid', 'voided', 'cancelled', 'refunded'].includes(item.paymentStatus) && item.amountDue > 0 && (
                          <span className="cell-stack-label text-red-600">{formatCurrency(item.amountDue)} due</span>
                        )}
                      </div>
                    </td>
                    {/* Date */}
                    <td className="cell-secondary">
                      {item.dueDate ? (
                        <div className="cell-stack">
                          <span className="cell-stack-label">Due:</span>
                          <span className="cell-stack-value">{formatDate(item.dueDate)}</span>
                        </div>
                      ) : (
                        formatDate(item.createdAt)
                      )}
                    </td>
                    {/* Details - Physician info */}
                    <td className="cell-secondary">
                      {item.physicianId ? (
                        <div className="cell-inline">
                          <User className="cell-inline-icon" />
                          <span className="cell-truncate">Dr. {item.physicianId.profile?.firstName} {item.physicianId.profile?.lastName}</span>
                        </div>
                      ) : (
                        <span className="cell-muted">-</span>
                      )}
                    </td>
                    {/* Actions */}
                    <td className="cell-actions">
                      <div className="table-actions">
                        <button
                          onClick={() => {
                            setSelectedItem(item);
                            openInvoiceDetail({ _id: item._id } as PopulatedInvoice);
                          }}
                          className="table-action-btn table-action-btn-secondary"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          <span className="hidden lg:inline">View</span>
                        </button>
                        {item.amountDue > 0 && !['paid', 'voided', 'cancelled', 'refunded'].includes(item.paymentStatus) && user.role !== 'supervisor' && (
                          <button
                            onClick={() => {
                              setPaymentInvoice(item);
                              setShowPaymentModal(true);
                            }}
                            className="table-action-btn table-action-btn-success"
                          >
                            <CreditCard className="h-3 w-3 mr-1" />
                            <span className="hidden lg:inline">Pay Now</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrevPage || loading}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 self-center">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNextPage || loading}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                  {' '}-{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.totalCount)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{pagination.totalCount}</span>
                  {' '}items
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevPage || loading}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        } disabled:opacity-50`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage || loading}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal - handles both bulk invoices and patient orders */}
      {showDetailsModal && (selectedInvoice || selectedItem) && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-[60] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedInvoice ? 'Invoice Details' : 'Order Details'}
                    </h3>
                    {selectedItem && (
                      <span className={getTypeBadge(selectedItem.type)}>
                        {selectedItem.type === 'bulk' ? 'Bulk' : 'Patient'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    {selectedInvoice && (
                      <button
                        onClick={() => printInvoice(selectedInvoice)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setSelectedInvoice(null);
                        setInvoiceShipment(null);
                        setSelectedItem(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Conditional content based on type */}
                {selectedInvoice ? (
                  <>
                    {/* Bulk Invoice Detail View */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Invoice Info */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Invoice Information</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Invoice Number:</span>
                              <span className="font-medium">{selectedInvoice.invoiceNumber}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Status:</span>
                              <span className={getStatusBadge(selectedInvoice.status)}>
                                {selectedInvoice.status.charAt(0).toUpperCase() + selectedInvoice.status.slice(1)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Created:</span>
                              <span>{formatDate(selectedInvoice.createdAt)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Due Date:</span>
                              <span>{formatDate(selectedInvoice.dueDate)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Payment Terms:</span>
                              <span className="capitalize">{selectedInvoice.paymentTerms}</span>
                            </div>
                          </div>
                        </div>

                        {/* Physician Info */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Physician</h4>
                          <div className="space-y-2 text-sm">
                            <div>Dr. {selectedInvoice.physicianId?.profile?.firstName} {selectedInvoice.physicianId?.profile?.lastName}</div>
                            <div className="text-gray-500">{selectedInvoice.physicianId?.email}</div>
                          </div>
                        </div>

                        {/* Clinic Address */}
                        {selectedInvoice.physicianId?.physicianData?.clinicAddress && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Clinic Address</h4>
                            <div className="space-y-1 text-sm">
                              {selectedInvoice.physicianId?.profile?.clinicName && (
                                <div className="font-medium">{selectedInvoice.physicianId.profile.clinicName}</div>
                              )}
                              <div className="text-gray-600">
                                {selectedInvoice.physicianId.physicianData.clinicAddress.street}
                              </div>
                              {selectedInvoice.physicianId.physicianData.clinicAddress.street2 && (
                                <div className="text-gray-600">
                                  {selectedInvoice.physicianId.physicianData.clinicAddress.street2}
                                </div>
                              )}
                              <div className="text-gray-600">
                                {selectedInvoice.physicianId.physicianData.clinicAddress.city}, {selectedInvoice.physicianId.physicianData.clinicAddress.state} {selectedInvoice.physicianId.physicianData.clinicAddress.zipCode}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Account Manager */}
                        {selectedInvoice.physicianId?.physicianData?.distributorId && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Account Manager</h4>
                            <div className="space-y-1 text-sm">
                              <div className="font-medium">{getAccountManagerName(selectedInvoice)}</div>
                              <div className="text-gray-500">{getAccountManagerEmail(selectedInvoice)}</div>
                            </div>
                          </div>
                        )}

                        {/* Order Created By */}
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Order Placed By</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center">
                              {selectedInvoice.createdByRole === 'admin' ? (
                                <User className="h-4 w-4 text-purple-500 mr-2" />
                              ) : (
                                <Building2 className="h-4 w-4 text-green-500 mr-2" />
                              )}
                              <span className="capitalize font-medium">
                                {selectedInvoice.createdByRole === 'admin' ? 'Admin' : 'Account Manager'}
                              </span>
                            </div>
                            {selectedInvoice.createdById && (
                              <>
                                <div>
                                  {selectedInvoice.createdById.profile?.firstName} {selectedInvoice.createdById.profile?.lastName}
                                </div>
                                <div className="text-gray-500">{selectedInvoice.createdById.email}</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Financial Info */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Financial Summary</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Subtotal:</span>
                              <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                            </div>
                            {selectedInvoice.shippingAmount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Shipping:</span>
                                <span>{formatCurrency(selectedInvoice.shippingAmount)}</span>
                              </div>
                            )}
                            {(selectedInvoice.telemedNetworkFee || 0) > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Telemed network fee:</span>
                                <span>{formatCurrency(selectedInvoice.telemedNetworkFee || 0)}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Tax:</span>
                              <span>{formatCurrency(selectedInvoice.taxAmount)}</span>
                            </div>
                            {(selectedInvoice as any).volumeReward?.discountAmount ? (
                              (selectedInvoice as any).volumeReward.appliedAs === 'physician_markup' ? (
                                <div className="flex justify-between text-indigo-600">
                                  <span>Volume Reward ({(selectedInvoice as any).volumeReward.tierLabel} {(selectedInvoice as any).volumeReward.discountPercent}%) — physician markup credit:</span>
                                  <span>+{formatCurrency((selectedInvoice as any).volumeReward.discountAmount)}</span>
                                </div>
                              ) : (
                                <div className="flex justify-between text-indigo-600">
                                  <span>Volume Reward ({(selectedInvoice as any).volumeReward.tierLabel} {(selectedInvoice as any).volumeReward.discountPercent}%):</span>
                                  <span>−{formatCurrency((selectedInvoice as any).volumeReward.discountAmount)}</span>
                                </div>
                              )
                            ) : null}
                            {/* Deal lines are exempt from the reward discount, so the
                                discount is less than N% of the invoice. Say why. */}
                            {(selectedInvoice as any).volumeReward?.excludedDealSubtotal ? (
                              <div className="text-xs text-gray-500 italic">
                                {formatCurrency((selectedInvoice as any).volumeReward.excludedDealSubtotal)} of
                                negotiated-deal pricing is excluded from the volume reward.
                              </div>
                            ) : null}
                            <div className="flex justify-between border-t pt-2">
                              <span className="font-medium">Total:</span>
                              <span className="font-medium">{formatCurrency(selectedInvoice.totalAmount)}</span>
                            </div>
                            {(selectedInvoice as any).creditApplied > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Credits Applied:</span>
                                <span className="text-green-600">-{formatCurrency((selectedInvoice as any).creditApplied)}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount Paid:</span>
                              <span className="text-green-600">{formatCurrency(selectedInvoice.amountPaid)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Amount Due:</span>
                              <span className="font-medium text-red-600">{formatCurrency(selectedInvoice.amountDue)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Shipping-only invoice: no line items, so show the parcels
                        that were actually dispatched. These are display rows
                        built from the shipment — deliberately NOT invoice.items,
                        which would pull carrier cost into commission payouts. */}
                    {(selectedInvoice as any).type === 'facility_shipping' && (
                      <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-2">
                          Shipping
                          {invoiceShipment?.shipDate && (
                            <span className="ml-2 text-sm font-normal text-gray-500">
                              {new Date(invoiceShipment.shipDate).toLocaleDateString()}
                            </span>
                          )}
                        </h4>
                        {invoiceShipment?.parcels?.length ? (
                          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parcel</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {invoiceShipment.parcels.map((p: any) => (
                                  <tr key={p.parcelId}>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      {[p.serviceLevel || p.carrier, p.trackingNumber].filter(Boolean).join(' · ') || `Parcel ${p.parcelId}`}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      {p.weight ? `${p.weight.value} ${p.weight.unit}` : '—'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                      {formatCurrency(p.ratedCost)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">
                            Shipping charge for a consolidated clinic delivery.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Items Table */}
                    {selectedInvoice.items.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 mb-2">Items</h4>
                      <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedInvoice.items.map((item, index) => (
                              <tr key={index} className={(item as any).isSupply ? 'bg-amber-50/50' : ''}>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {item.description}
                                  {(item as any).isSupply && (
                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Supply</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">{item.quantity}</td>
                                <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                                <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.totalPrice)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    )}

                    {/* Payment Status */}
                    {selectedInvoice.status === 'paid' && selectedInvoice.paidAt && (
                      <div className="mt-6">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center">
                            <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                            <div>
                              <div className="font-medium text-green-800">Invoice Fully Paid</div>
                              <div className="text-sm text-green-600">
                                Marked as paid on {formatDate(selectedInvoice.paidAt)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Payment Modal */}
      {paymentInvoice && (
        <InvoicePaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentInvoice(null);
          }}
          onSuccess={() => {
            setShowPaymentModal(false);
            setPaymentInvoice(null);
            loadPaymentTracking(pagination.page);
            toast.success('Payment successful! Your invoice has been paid.');
          }}
          invoice={{
            _id: paymentInvoice._id,
            invoiceNumber: paymentInvoice.displayNumber,
            amountDue: paymentInvoice.amountDue,
            physicianName: paymentInvoice.physicianId
              ? `Dr. ${paymentInvoice.physicianId.profile?.firstName || ''} ${paymentInvoice.physicianId.profile?.lastName || ''}`.trim()
              : undefined,
            clinicName: paymentInvoice.physicianId?.profile?.clinicName
          }}
          saveCard={true}
          title="Pay Invoice"
          returnPath="/invoice-tracking"
        />
      )}
    </div>
  );
};

export default InvoiceTracking;
